import { type DragEndEvent } from '@dnd-kit/core';
import { isDroppableOverData } from '../../../lib/isDroppableOverData';
import { isTreeRow } from '../../../lib/isTreeRow';
import { type TreeRow } from '../../../lib/types';

export function sortItemsAfterDrop<Attributes = unknown>(
    rows: TreeRow<Attributes>[],
    updateItem: (item: Attributes, options: { sequence?: number; parentId?: string | undefined }) => Attributes,
    { active, over }: DragEndEvent
): Attributes[] | void {
    const { data } = over ?? {};

    const overData = data?.current;
    const activeRow = active.data.current;

    if (!isDroppableOverData<Attributes>(overData) || !isTreeRow<Attributes>(activeRow)) {
        return;
    }

    const overRow = overData.node;

    if (activeRow.id === overRow?.id) {
        return;
    }

    const newSortedItems: Attributes[] = [];

    const queue = [...rows];

    let insertRowIndex: number | null = null;
    let insertParentId: string | undefined = undefined;

    if (isTreeRow(overRow)) {
        if (overData.type === 'node') {
            insertParentId = overRow.id;
            insertRowIndex = (overRow.index ?? 0) + (overRow.childrenIds.size ?? 0) + 1;
        } else {
            insertParentId = overRow.parentId;
            insertRowIndex = overRow.index;
        }
    }

    let i = 0;
    let insertRows = queue.splice(activeRow.index, activeRow.childrenIds.size + 1);

    const insertRowsWithParentId = () => {
        insertRows = insertRows.filter((row, index) => {
            let attributes = updateItem(row.attributes, { sequence: i });

            i++;

            if (index === 0) {
                attributes = updateItem(attributes, { parentId: insertParentId ?? '' });
            }

            newSortedItems.push(attributes);

            return false;
        });
    };

    while (queue.length > 0) {
        const node = queue.shift();

        if (!node) {
            continue;
        }

        if (node.index === insertRowIndex) {
            insertRowsWithParentId();
        }

        newSortedItems.push(updateItem(node.attributes, { sequence: i }));
        i++;
    }

    if (insertRows.length > 0) {
        insertRowsWithParentId();
    }

    return newSortedItems;
}
